import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// components
import Iconify from 'src/components/iconify';
//
import { Authorities } from 'src/utils/authorities';

// ----------------------------------------------------------------------

const icon = (name: string) => <Iconify icon={name} sx={{ width: 1, height: 1 }} />;

const ICONS = {
  dashboard: icon('solar:widget-5-bold-duotone'),
  clients: icon('solar:users-group-rounded-bold-duotone'),
  suppliers: icon('solar:box-bold-duotone'),
  quotations: icon('solar:document-text-bold-duotone'),
  contracts: icon('solar:file-check-bold-duotone'),
  services: icon('solar:widget-add-bold-duotone'),
  orders: icon('solar:cart-large-4-bold-duotone'),
  deliveries: icon('solar:delivery-bold-duotone'),
  tasks: icon('solar:checklist-minimalistic-bold-duotone'),
  finance: icon('solar:wallet-money-bold-duotone'),
  events: icon('solar:calendar-mark-bold-duotone'),
  transport: icon('solar:bus-bold-duotone'),
  stock: icon('solar:archive-bold-duotone'),
  documents: icon('solar:folder-with-files-bold-duotone'),
  reports: icon('solar:chart-square-bold-duotone'),
  users: icon('solar:user-id-bold-duotone'),
  audit: icon('solar:shield-keyhole-bold-duotone'),
  settings: icon('solar:settings-bold-duotone'),
  companies: icon('solar:buildings-3-bold-duotone'),
};

// ----------------------------------------------------------------------

type NavEntry = { pending?: boolean };

// Retira os modulos ainda sem paginas (Fases 3 a 5). Sem isto levam ao 404 e
// parecem links partidos; para os activar basta remover o `pending`.
function withoutPending<S extends { items: NavEntry[] }>(sections: S[]): S[] {
  return sections
    .map((section) => ({ ...section, items: section.items.filter((item) => !item.pending) }))
    .filter((section) => section.items.length > 0);
}

/**
 * Estrutura de navegacao dos modulos do sistema. Cada entrada declara em
 * `roles` as permissoes que dao acesso ao item: basta uma para o item aparecer.
 * O catalogo de permissoes esta em src/utils/authorities.ts e tem de coincidir
 * com o seed do backend.
 */
export function useNavData() {
  return useMemo(
    () => withoutPending([
      {
        subheader: 'Geral',
        items: [
          {
            title: 'Painel',
            path: paths.dashboard.root,
            icon: ICONS.dashboard,
          },
          {
            title: 'Relatórios',
            pending: true,
            path: paths.dashboard.report.root,
            icon: ICONS.reports,
            roles: [Authorities.READ_REPORTS],
            children: [
              { title: 'Financeiros', path: paths.dashboard.report.financial },
              { title: 'Comerciais', path: paths.dashboard.report.commercial },
              { title: 'Operacionais', path: paths.dashboard.report.operational },
            ],
          },
        ],
      },

      {
        subheader: 'Comercial',
        items: [
          {
            title: 'Clientes',
            path: paths.dashboard.client.list,
            icon: ICONS.clients,
            roles: [Authorities.READ_CLIENTS],
          },
          {
            title: 'Fornecedores',
            path: paths.dashboard.supplier.list,
            icon: ICONS.suppliers,
            roles: [Authorities.READ_SUPPLIERS],
          },
          {
            title: 'Serviços',
            path: paths.dashboard.service.list,
            icon: ICONS.services,
            roles: [Authorities.READ_SERVICES],
          },
          {
            title: 'Cotações',
            path: paths.dashboard.quotation.root,
            icon: ICONS.quotations,
            roles: [Authorities.READ_QUOTATIONS],
            children: [
              { title: 'Listar', path: paths.dashboard.quotation.list },
              {
                title: 'Nova cotação',
                path: paths.dashboard.quotation.new,
                roles: [Authorities.CREATE_QUOTATIONS],
              },
            ],
          },
          {
            title: 'Contratos',
            path: paths.dashboard.contract.root,
            icon: ICONS.contracts,
            roles: [Authorities.READ_CONTRACTS],
            children: [
              { title: 'Listar', path: paths.dashboard.contract.list },
              {
                title: 'Novo contrato',
                path: paths.dashboard.contract.new,
                roles: [Authorities.CREATE_CONTRACTS],
              },
            ],
          },
        ],
      },

      {
        subheader: 'Operações',
        items: [
          {
            title: 'Pedidos',
            pending: true,
            path: paths.dashboard.order.list,
            icon: ICONS.orders,
            roles: [Authorities.READ_ORDERS],
          },
          {
            title: 'Entregas',
            pending: true,
            path: paths.dashboard.delivery.list,
            icon: ICONS.deliveries,
            roles: [Authorities.READ_DELIVERIES],
          },
          {
            title: 'Tarefas',
            pending: true,
            path: paths.dashboard.task.list,
            icon: ICONS.tasks,
            roles: [Authorities.READ_TASKS],
          },
          {
            title: 'Eventos',
            pending: true,
            path: paths.dashboard.event.list,
            icon: ICONS.events,
            roles: [Authorities.READ_EVENTS],
          },
        ],
      },

      {
        subheader: 'Financeiro',
        items: [
          {
            title: 'Receitas',
            path: paths.dashboard.income.list,
            icon: ICONS.finance,
            roles: [Authorities.READ_INCOMES],
          },
          {
            title: 'Despesas',
            pending: true,
            path: paths.dashboard.expense.list,
            icon: ICONS.finance,
            roles: [Authorities.READ_EXPENSES],
          },
        ],
      },

      {
        subheader: 'Logística',
        items: [
          {
            title: 'Transporte',
            pending: true,
            path: paths.dashboard.vehicle.root,
            icon: ICONS.transport,
            roles: [
              Authorities.READ_VEHICLES,
              Authorities.READ_DRIVERS,
              Authorities.READ_ROUTES,
              Authorities.READ_TRIPS,
              Authorities.READ_MAINTENANCES,
            ],
            children: [
              {
                title: 'Viaturas',
                path: paths.dashboard.vehicle.list,
                roles: [Authorities.READ_VEHICLES],
              },
              {
                title: 'Motoristas',
                path: paths.dashboard.driver.list,
                roles: [Authorities.READ_DRIVERS],
              },
              {
                title: 'Rotas',
                path: paths.dashboard.route.list,
                roles: [Authorities.READ_ROUTES],
              },
              {
                title: 'Viagens',
                path: paths.dashboard.trip.list,
                roles: [Authorities.READ_TRIPS],
              },
              {
                title: 'Manutenções',
                path: paths.dashboard.maintenance.list,
                roles: [Authorities.READ_MAINTENANCES],
              },
            ],
          },
          {
            title: 'Stock',
            pending: true,
            path: paths.dashboard.product.root,
            icon: ICONS.stock,
            roles: [Authorities.READ_PRODUCTS, Authorities.READ_STOCK_MOVEMENTS],
            children: [
              {
                title: 'Produtos',
                path: paths.dashboard.product.list,
                roles: [Authorities.READ_PRODUCTS],
              },
              {
                title: 'Movimentos',
                path: paths.dashboard.stockMovement.list,
                roles: [Authorities.READ_STOCK_MOVEMENTS],
              },
            ],
          },
          {
            title: 'Documentos',
            pending: true,
            path: paths.dashboard.document.list,
            icon: ICONS.documents,
            roles: [Authorities.READ_DOCUMENTS],
          },
        ],
      },

      {
        subheader: 'Administração',
        items: [
          {
            title: 'Utilizadores',
            path: paths.dashboard.user.root,
            icon: ICONS.users,
            roles: [
              Authorities.READ_USERS,
              Authorities.READ_ROLES,
              Authorities.READ_PERMISSIONS,
            ],
            children: [
              {
                title: 'Listar',
                path: paths.dashboard.user.list,
                roles: [Authorities.READ_USERS],
              },
              {
                title: 'Novo Utilizador',
                path: paths.dashboard.user.new,
                roles: [Authorities.CREATE_USERS],
              },
              {
                title: 'Perfis',
                path: paths.dashboard.role.list,
                roles: [Authorities.READ_ROLES],
              },
              {
                title: 'Permissões',
                path: paths.dashboard.permission.list,
                roles: [Authorities.READ_PERMISSIONS],
              },
            ],
          },
          {
            title: 'Auditoria',
            pending: true,
            path: paths.dashboard.audit.list,
            icon: ICONS.audit,
            roles: [Authorities.READ_AUDIT],
          },
          {
            title: 'Definições',
            path: paths.dashboard.settings.root,
            icon: ICONS.settings,
            roles: [Authorities.READ_SETTINGS],
          },
          {
            title: 'Empresas',
            path: paths.dashboard.company.root,
            icon: ICONS.companies,
            roles: [Authorities.READ_COMPANIES],
            children: [
              { title: 'Listar', path: paths.dashboard.company.list },
              {
                title: 'Nova empresa',
                path: paths.dashboard.company.new,
                roles: [Authorities.CREATE_COMPANIES],
              },
            ],
          },
        ],
      },
    ]),
    []
  );
}
